import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartBar, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useDashboardList } from './hooks/useDashboards';
import WidgetWrapper from './components/WidgetWrapper';
import BarChart from './components/charts/BarChart';
import LineChart from './components/charts/LineChart';
import AreaChart from './components/charts/AreaChart';
import PieChart from './components/charts/PieChart';
import DonutChart from './components/charts/DonutChart';
import KpiCard from './components/charts/KpiCard';
import DataTable from './components/charts/DataTable';
import css from './DashboardBuilderPage.module.css';

// ─── Demo Data ────────────────────────────────────────────────────────────────

const DEMO_BAR = {
  columns: ['Month', 'Claims', 'Settlements'],
  rows: [
    { Month: 'Jan', Claims: 42, Settlements: 28 },
    { Month: 'Feb', Claims: 55, Settlements: 34 },
    { Month: 'Mar', Claims: 38, Settlements: 31 },
    { Month: 'Apr', Claims: 64, Settlements: 45 },
    { Month: 'May', Claims: 71, Settlements: 52 },
    { Month: 'Jun', Claims: 59, Settlements: 48 },
  ],
};

const DEMO_LINE = {
  columns: ['Week', 'New Cases', 'Closed Cases'],
  rows: [
    { Week: 'W1', 'New Cases': 12, 'Closed Cases': 8 },
    { Week: 'W2', 'New Cases': 18, 'Closed Cases': 14 },
    { Week: 'W3', 'New Cases': 15, 'Closed Cases': 19 },
    { Week: 'W4', 'New Cases': 22, 'Closed Cases': 16 },
    { Week: 'W5', 'New Cases': 28, 'Closed Cases': 21 },
    { Week: 'W6', 'New Cases': 19, 'Closed Cases': 24 },
    { Week: 'W7', 'New Cases': 25, 'Closed Cases': 20 },
    { Week: 'W8', 'New Cases': 30, 'Closed Cases': 27 },
  ],
};

const DEMO_AREA = {
  columns: ['Quarter', 'Revenue', 'Costs'],
  rows: [
    { Quarter: 'Q1 2024', Revenue: 125000, Costs: 82000 },
    { Quarter: 'Q2 2024', Revenue: 148000, Costs: 91000 },
    { Quarter: 'Q3 2024', Revenue: 167000, Costs: 95000 },
    { Quarter: 'Q4 2024', Revenue: 192000, Costs: 103000 },
    { Quarter: 'Q1 2025', Revenue: 210000, Costs: 110000 },
  ],
};

const DEMO_PIE = {
  columns: ['Case Type', 'Count'],
  rows: [
    { 'Case Type': 'PPI', Count: 342 },
    { 'Case Type': 'Housing Disrepair', Count: 189 },
    { 'Case Type': 'Scams', Count: 127 },
    { 'Case Type': 'Wills & Probate', Count: 95 },
    { 'Case Type': 'Other', Count: 64 },
  ],
};

const DEMO_TABLE = {
  columns: ['Client', 'Case Type', 'Status', 'Value', 'Opened'],
  rows: [
    {
      Client: 'Smith, J.',
      'Case Type': 'PPI',
      Status: 'Active',
      Value: 4250,
      Opened: '2025-01-15',
    },
    {
      Client: 'Brown, A.',
      'Case Type': 'Scams',
      Status: 'Pending',
      Value: 12800,
      Opened: '2025-02-03',
    },
    {
      Client: 'Patel, R.',
      'Case Type': 'Housing',
      Status: 'Active',
      Value: 8900,
      Opened: '2025-01-28',
    },
    {
      Client: 'Jones, M.',
      'Case Type': 'PPI',
      Status: 'Settled',
      Value: 3100,
      Opened: '2024-11-10',
    },
    {
      Client: 'Wilson, K.',
      'Case Type': 'Wills',
      Status: 'Active',
      Value: 6400,
      Opened: '2025-03-01',
    },
    {
      Client: 'Taylor, S.',
      'Case Type': 'Scams',
      Status: 'Active',
      Value: 22000,
      Opened: '2025-02-18',
    },
    {
      Client: 'Clark, D.',
      'Case Type': 'PPI',
      Status: 'Pending',
      Value: 5600,
      Opened: '2025-03-12',
    },
    {
      Client: 'Evans, L.',
      'Case Type': 'Housing',
      Status: 'Settled',
      Value: 14200,
      Opened: '2024-12-05',
    },
  ],
};

export default function DashboardBuilderPage() {
  const { isLoading } = useDashboardList();

  return (
    <div className={css.page}>
      <header className={css.header}>
        <div className={css.titleRow}>
          <FontAwesomeIcon icon={faChartBar} className={css.icon} />
          <h1>Dashboard Builder</h1>
          <span className={css.badge}>Beta</span>
        </div>
        <p className={css.subtitle}>
          Create custom dashboards with interactive charts, KPI cards, and data tables.
        </p>
      </header>

      <section className={css.content}>
        {isLoading ? (
          <div className={css.loading}>Loading dashboards…</div>
        ) : (
          <>
            {/* ── KPI row ── */}
            <div className={css.kpiRow}>
              <WidgetWrapper title="Active Cases">
                <KpiCard
                  value={817}
                  label="Active Cases"
                  delta={23}
                  deltaPct={2.9}
                  sparklineData={[740, 755, 780, 792, 800, 817]}
                />
              </WidgetWrapper>
              <WidgetWrapper title="Total Revenue">
                <KpiCard
                  value={842000}
                  label="Total Revenue"
                  format="currency"
                  delta={48000}
                  deltaPct={6.0}
                  sparklineData={[650000, 710000, 735000, 780000, 794000, 842000]}
                  conditionalRules={[
                    { rule: 'gte', threshold: 800000, colour: 'var(--success-500)' },
                  ]}
                />
              </WidgetWrapper>
              <WidgetWrapper title="Avg. Settlement">
                <KpiCard
                  value={6340}
                  label="Avg. Settlement"
                  format="currency"
                  delta={-210}
                  deltaPct={-3.2}
                  sparklineData={[6800, 6600, 6550, 6400, 6340]}
                  conditionalRules={[{ rule: 'lt', threshold: 6000, colour: 'var(--error-500)' }]}
                />
              </WidgetWrapper>
              <WidgetWrapper title="Conversion Rate">
                <KpiCard
                  value={72.4}
                  label="Conversion Rate"
                  format="percentage"
                  delta={1.8}
                  deltaPct={2.5}
                  sparklineData={[68, 69, 70, 71, 70.6, 72.4]}
                />
              </WidgetWrapper>
            </div>

            {/* ── Charts grid ── */}
            <div className={css.chartGrid}>
              <div className={css.chartCell}>
                <WidgetWrapper title="Claims vs Settlements">
                  <BarChart
                    data={DEMO_BAR}
                    dimensions={['Month']}
                    measures={[{ alias: 'Claims' }, { alias: 'Settlements' }]}
                  />
                </WidgetWrapper>
              </div>
              <div className={css.chartCell}>
                <WidgetWrapper title="Weekly Case Flow">
                  <LineChart
                    data={DEMO_LINE}
                    dimensions={['Week']}
                    measures={[{ alias: 'New Cases' }, { alias: 'Closed Cases' }]}
                    smooth
                  />
                </WidgetWrapper>
              </div>
              <div className={css.chartCell}>
                <WidgetWrapper title="Revenue vs Costs">
                  <AreaChart
                    data={DEMO_AREA}
                    dimensions={['Quarter']}
                    measures={[{ alias: 'Revenue' }, { alias: 'Costs' }]}
                  />
                </WidgetWrapper>
              </div>
              <div className={css.chartCell}>
                <WidgetWrapper title="Cases by Type">
                  <PieChart
                    data={DEMO_PIE}
                    dimensions={['Case Type']}
                    measures={[{ alias: 'Count' }]}
                  />
                </WidgetWrapper>
              </div>
              <div className={css.chartCell}>
                <WidgetWrapper title="Cases by Type (Donut)">
                  <DonutChart
                    data={DEMO_PIE}
                    dimensions={['Case Type']}
                    measures={[{ alias: 'Count' }]}
                  />
                </WidgetWrapper>
              </div>
              <div className={css.chartCell}>
                <WidgetWrapper title="Stacked Bar — Claims by Month">
                  <BarChart
                    data={DEMO_BAR}
                    dimensions={['Month']}
                    measures={[{ alias: 'Claims' }, { alias: 'Settlements' }]}
                    stacked
                    horizontal
                  />
                </WidgetWrapper>
              </div>
            </div>

            {/* ── Data table ── */}
            <div className={css.tableSection}>
              <WidgetWrapper title="Recent Cases">
                <DataTable data={DEMO_TABLE} pageSize={5} />
              </WidgetWrapper>
            </div>

            {/* ── Create CTA ── */}
            <div className={css.emptyState}>
              <button className={css.createBtn} disabled>
                <FontAwesomeIcon icon={faPlus} /> New Dashboard
                <span className={css.comingSoon}>Coming in Phase 3</span>
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
